/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { Dropdown, DatePicker, LinkButton, Button } from '@cambridgeassessment/cambridge-ui';
import CheckIcon from '@material-ui/icons/Check';
import {
  BUSINESS_UNITS,
  ComponentSeries,
  SubmissionWindowBase,
  SubmissionWindowName,
} from '@cambridgeassessment/lib-ee-commons';
import {
  StyledRow,
  StyledColumn,
  StyledActionsContainer,
  StyledDivider,
  ButtonText,
  StyledInputFieldContainer,
  StyledContainer,
  StyledSeriesLabel,
  StyledFieldDescription,
  StyledDisclaimer,
  useStyles,
} from './styles';
import { DATE_DISPLAY_FORMAT as DISPLAY_FORMAT } from '../../../../constants';
import { RootState } from '../../../../store';
import { noop } from 'lodash';
import { brandedFC } from '../../../../branding/brandedFC';
import { ComponentWithSeriesDates } from '../../../../types/Components';

interface Props {
  series: Partial<ComponentWithSeriesDates>;
  availableSeries: Partial<ComponentWithSeriesDates>[];
  dataQaId: string;
  isLocalSeries?: boolean;
  disabled?: boolean;
  startMaxDate?: string;
  endMaxDate?: string;
  submit: (seriesID: ComponentSeries['id'], series: ComponentSeries['submissionWindow']) => void;
  remove: (seriesID: ComponentSeries['id']) => void;
  removeLocalSeries?: () => void;
  updateSeriesStartDate: (seriesID: ComponentSeries['id'], startDate: string) => void;
  updateSeriesEndDate: (seriesID: ComponentSeries['id'], startDate: string) => void;
  updatePersistStartDateFlag: (seriesID: ComponentSeries['id'], persistStartDate: boolean) => void;
  updatePersistEndDateFlag: (seriesID: ComponentSeries['id'], persistStartDate: boolean) => void;
  setLocalSeries: (series: Partial<ComponentSeries>) => void | null;
}

const SeriesFC: React.FC<Props> = ({
  series,
  availableSeries,
  dataQaId,
  isLocalSeries = false,
  disabled,
  startMaxDate,
  endMaxDate,
  submit,
  remove,
  removeLocalSeries,
  updateSeriesStartDate,
  updateSeriesEndDate,
  updatePersistStartDateFlag,
  updatePersistEndDateFlag,
  setLocalSeries = noop,
}): React.ReactElement => {
  const dispatch = useDispatch();
  const businessUnit = useSelector((state: RootState) => state.userData.businessUnit);
  const { isLoading } = useSelector((state: RootState) => state.series);
  const [selectedSubmissionWindow, setSelectedSubmissionWindow] = useState<SubmissionWindowBase | null>(null);
  const [startDate, setStartDate] = useState<string>(series.submissionWindow?.start || '');
  const [endDate, setEndDate] = useState<string>(series.submissionWindow?.end || '');
  const [isFutureSeries, setIsFutureSeries] = useState<boolean>(false);
  const [bufferDate, setBufferDate] = useState<string>('');
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(true);

  const areDatesDisabled = (selectedSubmissionWindow == null || series.name === '') && isLocalSeries;

  const changeSeriesEndDate = useCallback(
    (date: moment.Moment): void => {
      const endDateValue = date.toISOString();
      setEndDate(endDateValue);
      if (!isLocalSeries) {
        updateSeriesEndDate((series as ComponentSeries).id, endDateValue);

        if (!series.submissionWindow?.persistEndDate && businessUnit?.id === BUSINESS_UNITS.CIE.id) {
          updatePersistEndDateFlag((series as ComponentSeries).id, true);
        }
      }
    },
    [businessUnit?.id, isLocalSeries, series, updatePersistEndDateFlag, updateSeriesEndDate],
  );

  const changeSeriesStartDate = useCallback(
    (date: moment.Moment): void => {
      const startDateValue = date.toISOString();
      setStartDate(startDateValue);
      if (!isLocalSeries) {
        updateSeriesStartDate((series as ComponentSeries).id, startDateValue);

        if (!series.submissionWindow?.persistStartDate && businessUnit?.id === BUSINESS_UNITS.CIE.id) {
          updatePersistStartDateFlag((series as ComponentSeries).id, true);
        }
      }
    },
    [businessUnit?.id, isLocalSeries, series, updatePersistStartDateFlag, updateSeriesStartDate],
  );

  const removeItem = (): void => {
    remove((series as ComponentSeries).id);
  };

  const onSeriesSelect = useCallback(
    (series: Partial<ComponentSeries>): void => {
      if (businessUnit && series.id) {
        setLocalSeries(series);
      }
    },
    [setLocalSeries, businessUnit],
  );

  useEffect(() => {
    if (isLocalSeries && selectedSubmissionWindow) {
      setBufferDate(selectedSubmissionWindow?.buffer as string);
    } else {
      setBufferDate(series.submissionWindow?.buffer as string);
    }
  }, [isLocalSeries, selectedSubmissionWindow, series]);

  useEffect(() => {
    if (!isLoading && series?.submissionWindows?.length === 1) {
      setSelectedSubmissionWindow(series.submissionWindows[0]);
    }
    if (series) {
      const today = moment();
      const isFutureStartDate = moment(series.startDate).isAfter(today);
      const isFutureEndDate = moment(series.endDate).isAfter(today);
      const isFutureBufferDate = moment(bufferDate).isAfter(today);

      setIsFutureSeries(isFutureStartDate && isFutureEndDate && isFutureBufferDate);
    }
  }, [dispatch, isLoading, bufferDate, series]);

  useEffect(() => {
    if (isLocalSeries && selectedSubmissionWindow) {
      setBufferDate(selectedSubmissionWindow?.buffer as string);
    } else {
      setBufferDate(series.submissionWindow?.buffer as string);
    }
  }, [isLocalSeries, selectedSubmissionWindow, series]);

  useEffect(() => {
    if (isLocalSeries && series.startDate && series.endDate) {
      const swStartDate = series.startDate;
      const dayBeforeBufferDate = moment.utc(bufferDate).subtract(1, 'd').toISOString();

      if (isFutureSeries) {
        const monthLaterDate = moment.utc(swStartDate).add(30, 'd');

        if (moment(swStartDate).isSame(monthLaterDate, 'd')) {
          setEndDate(moment.utc(swStartDate).add(1, 'd').toISOString());
        } else if (monthLaterDate.isSameOrAfter(bufferDate, 'd')) {
          setEndDate(dayBeforeBufferDate);
        } else {
          setEndDate(moment.utc(monthLaterDate).toISOString());
        }
      } else {
        const isAfterBufferDate = moment.utc(swStartDate).endOf('m').startOf('d').isSameOrAfter(bufferDate, 'd');
        setEndDate(isAfterBufferDate ? dayBeforeBufferDate : series.endDate);
      }
      setStartDate(swStartDate);
    }
  }, [isLocalSeries, bufferDate, series, isFutureSeries]);

  useEffect(() => {
    setSubmitDisabled(!series || !selectedSubmissionWindow || !endDate || !startDate);
  }, [series, selectedSubmissionWindow, startDate, endDate, bufferDate]);

  const onSubmit = (): void => {
    const submissionWindow = {
      name: selectedSubmissionWindow?.name as SubmissionWindowName,
      buffer: selectedSubmissionWindow?.buffer,
      start: startDate,
      end: endDate,
    };

    submit((series as ComponentSeries).id, submissionWindow);
  };

  useEffect(() => {
    if (startMaxDate && startMaxDate < startDate) {
      changeSeriesStartDate(moment(startMaxDate));
    }
  }, [startMaxDate, changeSeriesStartDate, startDate]);

  useEffect(() => {
    if (endMaxDate && endMaxDate < endDate) {
      changeSeriesEndDate(moment(endMaxDate));
    }
  }, [endMaxDate, changeSeriesEndDate, endDate]);

  const seriesStartMaxDate = () => {
    const startingMaxDate = moment(startMaxDate);
    const defaultEndDate = moment(endDate).subtract(1, 'd');
    if (startMaxDate && startingMaxDate < defaultEndDate) {
      return startingMaxDate;
    }

    return defaultEndDate;
  };

  const seriesEndMaxDate = () => {
    const endingMaxDate = moment(endMaxDate);
    const defaultEndDate = moment(bufferDate).subtract(1, 'd');
    if (endMaxDate && endingMaxDate < defaultEndDate) {
      return endingMaxDate;
    }

    return defaultEndDate;
  };

  const renderSeriesName = (seriesValue: Partial<ComponentSeries>): string => seriesValue.name || '';
  const renderSeriesSubmissionWindowName = (submissionWindow: SubmissionWindowBase): SubmissionWindowName =>
    submissionWindow.name;

  const classes = useStyles();

  const StartDateDisclaimer = brandedFC(({ showSelectSeriesDateDisclaimer }) =>
    showSelectSeriesDateDisclaimer ? (
      <StyledDisclaimer data-qa-id={`${dataQaId}-select-start-date-disclaimer`}>
        Automatically calculated, based on submission deadline in CI public website, but can be overridden, if
        appropriate
      </StyledDisclaimer>
    ) : null,
  );

  const EndDateDisclaimer = brandedFC(({ showSelectSeriesDateDisclaimer }) =>
    showSelectSeriesDateDisclaimer ? (
      <StyledDisclaimer data-qa-id={`${dataQaId}-select-end-date-disclaimer`}>
        Fetched from CI public website, but can be overridden, if appropriate
      </StyledDisclaimer>
    ) : null,
  );

  return (
    <StyledContainer>
      <StyledRow>
        <StyledColumn>
          <StyledSeriesLabel marginBottom={isLocalSeries ? 16 : 4}>
            {isLocalSeries ? (
              'Select Series'
            ) : (
              <>
                <StyledFieldDescription data-qa-id={`${dataQaId}-series-name-${series.name}`}>
                  Series name: <b>{series.name}</b>
                </StyledFieldDescription>
                <StyledFieldDescription data-qa-id={`${dataQaId}-series-sw-${series.name}`}>
                  Submission window name: <b>{series.submissionWindow?.name}</b>
                </StyledFieldDescription>
              </>
            )}
          </StyledSeriesLabel>
          {isLocalSeries && (
            <StyledInputFieldContainer addMargin>
              <Dropdown
                data={availableSeries || []}
                renderOption={renderSeriesName}
                renderSelectedOption={renderSeriesName}
                dataQaId={`${dataQaId}-series-selector-${series.name}`}
                onChange={onSeriesSelect}
                value={availableSeries.find((it) => it.id === series?.id) || null}
                placeholder='Please choose a series'
              />
            </StyledInputFieldContainer>
          )}
        </StyledColumn>
        <StyledColumn>
          {isLocalSeries && <StyledSeriesLabel marginBottom={16}>Select Submission window</StyledSeriesLabel>}
          {isLocalSeries && (
            <StyledInputFieldContainer>
              <Dropdown
                data={series.submissionWindows || []}
                renderOption={renderSeriesSubmissionWindowName}
                renderSelectedOption={renderSeriesSubmissionWindowName}
                dataQaId={`${dataQaId}-series-submission-window-selector-${series.name}`}
                onChange={setSelectedSubmissionWindow}
                value={selectedSubmissionWindow}
                placeholder='Please choose a submission window'
              />
            </StyledInputFieldContainer>
          )}
        </StyledColumn>
      </StyledRow>
      <StyledRow>
        <StyledColumn>
          <StyledSeriesLabel>Select start date</StyledSeriesLabel>
          <StyledInputFieldContainer addMargin>
            <DatePicker
              key={startDate}
              value={startDate}
              onChange={changeSeriesStartDate}
              autoOk={true}
              disabled={disabled || areDatesDisabled}
              format={DISPLAY_FORMAT}
              minDate={moment(series.startDate)}
              maxDate={seriesStartMaxDate()}
              maxDateMessage=''
              dataQaId={`${dataQaId}-start-date-selector-${series.name}`}
            />
            <StartDateDisclaimer />
          </StyledInputFieldContainer>
        </StyledColumn>
        <StyledColumn>
          <StyledSeriesLabel>Select end date</StyledSeriesLabel>
          <StyledInputFieldContainer>
            <DatePicker
              key={endDate}
              value={endDate}
              onChange={changeSeriesEndDate}
              autoOk={true}
              disabled={disabled || areDatesDisabled}
              format={DISPLAY_FORMAT}
              minDate={moment(startDate).add(1, 'd')}
              minDateMessage=''
              maxDate={seriesEndMaxDate()}
              dataQaId={`${dataQaId}-end-date-selector-${series.name}`}
            />
            <EndDateDisclaimer />
          </StyledInputFieldContainer>
        </StyledColumn>
      </StyledRow>
      <StyledActionsContainer>
        {isLocalSeries && (
          <>
            <Button
              onClick={removeLocalSeries}
              qaId={`${dataQaId}-series-cancel-button-${series.name}`}
              variant='text'
              type='submit'
              size='small'
            >
              <ButtonText>Cancel</ButtonText>
            </Button>
            <Button
              onClick={onSubmit}
              qaId={`${dataQaId}-series-add-button-${series.name}`}
              variant='outlined'
              type='submit'
              size='small'
              disabled={submitDisabled}
              startIcon={<CheckIcon />}
            >
              Save
            </Button>
          </>
        )}
        {!isLocalSeries && !disabled && (
          <LinkButton
            arrowType='up'
            endIcon={false}
            onClick={removeItem}
            dataQaId={`${dataQaId}-series-remove-button-${series.name}`}
            passedClassName={classes.root}
            disabled={disabled}
            ariaLabel={'Remove'}
          >
            Remove
          </LinkButton>
        )}
      </StyledActionsContainer>
      <StyledDivider />
    </StyledContainer>
  );
};

export default SeriesFC;
